import React, { useState, useEffect } from "react";
import { Plus, KeyRound, Info, Search, X } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/Badges";
import { TempPwModal } from "./StaffUsers";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const CROLES = [{ value: "customer_admin", label: "Customer Admin" }, { value: "customer_viewer", label: "Viewer" }];

export default function CustomerUsers({ embedded = false }) {
  const { can } = useAuth();
  const canEdit = can("users", "edit");
  const [refresh, setRefresh] = useState(0);
  const [adding, setAdding] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [form, setForm] = useState({ customer_id: "", name: "", email: "", role: "customer_viewer" });
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (custQ.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => api.get("/customers", { params: { search: custQ, limit: 200 } }).then((r) => setCustResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [custQ]);

  const create = async () => {
    if (!customer) return toast.error("Select a customer");
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try {
      const r = await api.post("/users/customers", { ...form, customer_id: customer.id });
      if (r.data.temp_password) setTempPw({ email: r.data.email, pw: r.data.temp_password });
      toast.success("Customer user created");
      setAdding(false); setForm({ customer_id: "", name: "", email: "", role: "customer_viewer" }); setCustomer(null); setCustQ("");
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const resetPw = async (row) => {
    try { const r = await api.post(`/users/customers/${row.id}/reset-password`); setTempPw({ email: row.email, pw: r.data.temp_password }); toast.success("Password reset"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "name", label: "Name", sortable: true, editable: true, render: (v) => <span className="t-strong">{v || "—"}</span> },
    { key: "email", label: "Email", sortable: true, editable: true, mono: true },
    { key: "customer_name", label: "Customer", sortable: true, render: (v) => v || "—" },
    { key: "role", label: "Access", sortable: true, editable: true, type: "select", options: CROLES, render: (v) => <Badge label={CROLES.find((r) => r.value === v)?.label || v} cls={v === "customer_admin" ? "b-gold" : "b-slate"} /> },
    { key: "active", label: "Active", sortable: true, editable: true, type: "select", options: [{ value: "true", label: "Active" }, { value: "false", label: "Disabled" }], render: (v) => v === false ? <Badge label="Disabled" cls="b-red" /> : <Badge label="Active" cls="b-green" /> },
    { key: "must_reset", label: "Status", render: (v) => v ? <Badge label="Reset pending" cls="b-amber" /> : <Badge label="Set" cls="b-green" /> },
    { key: "last_login_at", label: "Last login", sortable: true, render: (v) => <span className="mono t-mut">{fmtDate(v)}</span> },
    { key: "id", label: "", render: (_, row) => canEdit ? <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); resetPw(row); }} data-testid={`cu-reset-${row.id}`}><KeyRound size={13} /> Reset</button> : null },
  ];

  return (
    <>
      {!embedded && (
        <PageHead crumb="Administration" title="Customer Users"
          sub="All customer-portal logins, managed centrally by PTE. Each user is locked to one customer and sees only that customer's data."
          actions={canEdit && <button className="btn gold" onClick={() => setAdding(true)} data-testid="add-cu-btn"><Plus size={15} /> Add customer user</button>} />
      )}
      {embedded && canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button className="btn gold" onClick={() => setAdding(true)} data-testid="add-cu-btn"><Plus size={15} /> Add customer user</button>
        </div>
      )}

      <DataTable endpoint="/users/customers" columns={columns} rowKey="id" testid="customer-users"
        canEdit={canEdit} editEndpoint={(row) => `/users/customers/${row.id}`}
        defaultSort={{ by: "name", dir: "asc" }} searchPlaceholder="Search name or email…"
        refreshSignal={refresh} />

      {adding && (
        <Modal open onClose={() => setAdding(false)} icon={Plus} title="Add customer user"
          footer={<><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button><button className="btn gold" onClick={create} data-testid="save-cu-btn">Create user</button></>}>
          <div className="fld" style={{ position: "relative", marginBottom: 14 }}>
            <label>Customer <span className="req">*</span></label>
            {customer ? (
              <div className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span className="t-strong">{customer.customer_name}</span>
                <button className="icon-btn" style={{ width: 24, height: 24 }} onClick={() => setCustomer(null)}><X size={14} /></button>
              </div>
            ) : (
              <>
                <div className="input" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Search size={14} color="var(--text-3)" />
                  <input style={{ border: "none", outline: "none", width: "100%", background: "none" }} placeholder="Search customer…" value={custQ} onChange={(e) => setCustQ(e.target.value)} data-testid="cu-cust-search" />
                </div>
                {custResults.length > 0 && (
                  <div className="card" style={{ position: "absolute", zIndex: 10, left: 0, right: 0, maxHeight: 200, overflowY: "auto" }}>
                    {custResults.map((c) => <button key={c.id} className="nav-item" style={{ color: "var(--text)", borderRadius: 0 }} onClick={() => { setCustomer(c); setCustResults([]); }} data-testid={`cu-opt-${c.id}`}>{c.customer_name}</button>)}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="form-grid">
            <div className="fld"><label>Full name <span className="req">*</span></label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cu-name" /></div>
            <div className="fld"><label>Email <span className="req">*</span></label><input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cu-email" /></div>
            <div className="fld"><label>Access level</label><select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{CROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
          </div>
          <div className="notice" style={{ marginTop: 14 }}><Info size={16} /><span>A temporary password is generated; the customer sets their own on first sign-in.</span></div>
        </Modal>
      )}

      <TempPwModal data={tempPw} onClose={() => setTempPw(null)} />
    </>
  );
}
